import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Grid, Text, VStack, useT } from '@minetrace/ui'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LuMapPin as MapPin,
  LuBadgeCheck as BadgeCheck,
  LuArrowRight as ArrowRight,
  LuMap as MapIcon,
} from 'react-icons/lu'
import { KMap, AdvancedMarker, FitBounds, MarkerTooltip, useMap } from '../../lib/googleMaps'
import { ComponentPin } from '../../lib/mapMarkers'
import { programacionesService } from '../../api/services'
import type { ScheduleResponse } from '../../api/models/response/programacion'
import { SCHEDULE_STATUSES, DETAIL_STATUSES } from '../../data/enums'
import ubigeoData from '../../data/ubigeo.json'

// ─── Design-system palette ────────────────────────────────────────────────────
const P = {
  primaryContainer: '#b4532a',
  primary: '#012d1d',
  onPrimary: '#ffffff',
  secondary: '#51634d',
  surface: '#f7faf8',
  surfaceContainerLow: '#f1f4f2',
  surfaceContainerLowest: '#ffffff',
  surfaceContainer: '#ebefed',
  outlineVariant: '#c1c8c2',
  onSurface: '#2a2622',
  onSurfaceVariant: '#414844',
  primaryFixed: '#c1ecd4',
  onPrimaryFixedVariant: '#274e3d',
  secondaryContainer: '#d1e6ca',
  onSecondaryContainer: '#556751',
} as const

// ─── Ubigeo helpers ───────────────────────────────────────────────────────────
type UbigeoJson = Record<string, {
  name: string
  provinces: Record<string, {
    name: string
    districts: Record<string, { name: string }>
  }>
}>

const ubigeo = ubigeoData as UbigeoJson

function resolveUbigeo(code: string | null): string {
  if (!code || code.length < 2) return '-'
  const depto = ubigeo[code.substring(0, 2)]
  if (!depto) return code
  if (code.length < 4) return depto.name
  const prov = depto.provinces[code.substring(0, 4)]
  if (!prov) return depto.name
  if (code.length < 6) return `${depto.name} / ${prov.name}`
  const dist = prov.districts[code]
  if (!dist) return `${depto.name} / ${prov.name}`
  return `${depto.name} / ${prov.name} / ${dist.name}`
}

// ─── Motion wrapper ───────────────────────────────────────────────────────────
const MotionBox = motion.create(Box)

// ─── Sub-components ───────────────────────────────────────────────────────────

function CategoryLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      fontSize="10px"
      textTransform="uppercase"
      letterSpacing="0.12em"
      fontWeight="800"
      color={P.onSurfaceVariant}
      mb={1}
    >
      {children}
    </Text>
  )
}

// InfoFieldBlock removed - using inline layout instead

function StatusBadge({ label }: { label: string }) {
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      bg={P.primaryFixed}
      color={P.onPrimaryFixedVariant}
      px={3}
      py="3px"
      borderRadius="full"
      fontSize="11px"
      fontWeight="800"
      letterSpacing="0.04em"
    >
      {label}
    </Box>
  )
}

// DateRangeBlock removed - using inline layout instead

// ─── Map helpers ─────────────────────────────────────────────────────────────
type LL = google.maps.LatLngLiteral

/**
 * Polígono imperativo sobre `google.maps.Polygon` (la base no exporta uno).
 * Mismo patrón de ciclo de vida que `lib/googleMaps/Polyline`.
 */
function MapPolygon({
  paths,
  strokeColor,
  fillColor,
  fillOpacity = 0.15,
  strokeWeight = 2,
}: {
  paths: LL[]
  strokeColor: string
  fillColor?: string
  fillOpacity?: number
  strokeWeight?: number
}) {
  const map = useMap()
  const polyRef = useRef<google.maps.Polygon | null>(null)

  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    polyRef.current = poly
    return () => {
      poly.setMap(null)
      polyRef.current = null
    }
  }, [map])

  useEffect(() => {
    polyRef.current?.setOptions({
      paths,
      strokeColor,
      strokeWeight,
      fillColor: fillColor ?? strokeColor,
      fillOpacity,
      clickable: false,
    })
  }, [paths, strokeColor, strokeWeight, fillColor, fillOpacity])

  return null
}

// ─── Page component ───────────────────────────────────────────────────────────

export default function DetalleProgramacionPage() {
  const navigate = useNavigate()
  const { t } = useT()
  const { id } = useParams()
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null)
  const [loading, setLoading] = useState(!!id)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    programacionesService.getById(id)
      .then((data) => {
        if (!cancelled) {
          setSchedule(data)
          if (data.code) sessionStorage.setItem('current_schedule_code', data.code)
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    try {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateStr
    }
  }

  // ── Derived data (hooks must be before early returns) ───────────────────────
  // Updated date display
  const updatedAgo = schedule ? new Date(schedule.updatedAt ?? schedule.createdAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) : ''

  const detailsByCompany = useMemo(() => {
    const map = new Map<string, { companyName: string; items: NonNullable<ScheduleResponse['details']> }>()
    for (const d of schedule?.details ?? []) {
      const existing = map.get(d.companyId)
      if (existing) {
        existing.items = [...existing.items, d]
      } else {
        map.set(d.companyId, { companyName: d.companyName, items: [d] })
      }
    }
    return map
  }, [schedule])

  // ── Map geometry (polygons + component pins) ────────────────────────────────
  const mapPolygons = useMemo(() => {
    const result: { id: string; paths: LL[] }[] = []
    for (const d of schedule?.details ?? []) {
      if (!d.boundaryGeoJson) continue
      try {
        const geojson = JSON.parse(d.boundaryGeoJson)
        const paths = (geojson.coordinates[0] as number[][]).map((c) => ({ lat: c[1], lng: c[0] }))
        result.push({ id: d.id, paths })
      } catch {
        /* ignore */
      }
    }
    return result
  }, [schedule])

  const componentPins = useMemo(() => {
    const result: { id: string; pos: LL; name: string | null; typeName: string | null; typeCode: string | null }[] = []
    for (const d of schedule?.details ?? []) {
      for (const c of d.components ?? []) {
        if (c.latitude != null && c.longitude != null) {
          result.push({ id: c.id, pos: { lat: c.latitude, lng: c.longitude }, name: c.name, typeName: c.typeName, typeCode: c.typeCode })
        }
      }
    }
    return result
  }, [schedule])

  const mapAllPoints = useMemo<LL[]>(
    () => [...mapPolygons.flatMap((p) => p.paths), ...componentPins.map((c) => c.pos)],
    [mapPolygons, componentPins],
  )

  // ── Loading / empty states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Box minH="60vh" display="flex" alignItems="center" justifyContent="center">
        <Text tid="programaciones.detail.loading" fontSize="sm" color={P.onSurfaceVariant} />
      </Box>
    )
  }

  if (!schedule) {
    return (
      <Box minH="60vh" display="flex" alignItems="center" justifyContent="center">
        <Text tid="programaciones.detail.notFound" fontSize="sm" color={P.onSurfaceVariant} />
      </Box>
    )
  }

  const scheduleStatusLabel = SCHEDULE_STATUSES.find((s) => s.value === schedule.scheduleStatus)?.label ?? t('programaciones.detail.unknown')

  const locationLabel = resolveUbigeo(schedule.ubigeoCode)

  return (
    <Box pb={12}>
      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <MotionBox
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        mb={8}
      >
        <Flex alignItems="flex-start" gap={4} flexWrap="wrap">
          <Box flex={1} minW={0}>
            <Flex alignItems="center" gap={3} flexWrap="wrap" mb={1}>
              <Text
                fontSize="36px"
                fontWeight="800"
                color={P.onSurface}
                lineHeight="1.1"
                letterSpacing="-0.02em"
                fontFamily="'Bricolage Grotesque', sans-serif"
              >
                {schedule.code ?? t('programaciones.detail.draft')}
              </Text>
              <StatusBadge label={scheduleStatusLabel} />
            </Flex>
            <Text tid="programaciones.detail.updatedLabel" values={{ date: updatedAgo }} fontSize="sm" color={P.onSurfaceVariant} />
          </Box>
        </Flex>
      </MotionBox>

      {/* ── Bento grid ──────────────────────────────────────────────────────── */}
      <Grid
        templateColumns={{ base: '1fr', lg: 'repeat(12, 1fr)' }}
        gap={5}
        alignItems="start"
      >
        {/* ── Left column (4 / 12) ── */}
        <Box
          style={{ gridColumn: 'span 4' } as React.CSSProperties}
        >
          <VStack gap={5} alignItems="stretch">

            {/* Datos Generales card */}
            <MotionBox
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              bg={P.surfaceContainerLow}
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              borderRadius="xl"
              p={6}
            >
              {/* Section header with icon */}
              <Flex alignItems="center" gap={2} mb={5}>
                <Box color={P.primaryContainer} flexShrink={0}>
                  <MapPin size={18} />
                </Box>
                <Text tid="programaciones.detail.generalDataTitle" fontFamily="'Bricolage Grotesque', sans-serif" fontWeight="700" fontSize="lg" color={P.primaryContainer} />
              </Flex>

              <VStack gap={4} alignItems="stretch">
                {/* Nombre + Codigo en grid 2 cols */}
                <Grid templateColumns="1fr 1fr" gap={4}>
                  <Box>
                    <CategoryLabel>{t('programaciones.detail.nameLabel')}</CategoryLabel>
                    <Text fontSize="sm" fontWeight="600" color={P.onSurface}>
                      {schedule.name || schedule.code || t('programaciones.detail.noName')}
                    </Text>
                  </Box>
                  <Box>
                    <CategoryLabel>{t('programaciones.detail.codeLabel')}</CategoryLabel>
                    <Text fontSize="sm" fontWeight="600" color={P.onSurface}>
                      {schedule.code ?? t('programaciones.detail.draft')}
                    </Text>
                  </Box>
                </Grid>

                {/* Separador + Periodo */}
                <Box pt={3} borderTop="1px solid" borderColor={`${P.outlineVariant}20`}>
                  <CategoryLabel>{t('programaciones.detail.inspectionPeriod')}</CategoryLabel>
                  <Flex alignItems="center" gap={3} mt={2}>
                    <Box bg={P.surfaceContainerLowest} p={3} borderRadius="lg" flex={1} textAlign="center">
                      <Text tid="programaciones.detail.dateStart" fontSize="10px" fontWeight="700" color={P.secondary} textTransform="uppercase" letterSpacing="0.05em" />
                      <Text fontSize="sm" fontWeight="700" color={P.onSurface}>
                        {formatDate(schedule.startDate)}
                      </Text>
                    </Box>
                    <Box flexShrink={0} color={P.outlineVariant}>
                      <ArrowRight size={16} />
                    </Box>
                    <Box bg={P.surfaceContainerLowest} p={3} borderRadius="lg" flex={1} textAlign="center">
                      <Text tid="programaciones.detail.dateEnd" fontSize="10px" fontWeight="700" color={P.secondary} textTransform="uppercase" letterSpacing="0.05em" />
                      <Text fontSize="sm" fontWeight="700" color={P.onSurface}>
                        {formatDate(schedule.endDate)}
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                {/* Ubicacion si existe */}
                {schedule.ubigeoCode && (
                  <Box>
                    <CategoryLabel>{t('programaciones.detail.locationLabel')}</CategoryLabel>
                    <Flex alignItems="center" gap={1.5} mt={0.5}>
                      <MapPin size={13} color={P.secondary} />
                      <Text fontSize="sm" fontWeight="500" color={P.onSurface}>
                        {locationLabel}
                      </Text>
                    </Flex>
                  </Box>
                )}

              </VStack>
            </MotionBox>

            {/* Geologos card */}
            <MotionBox
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
              bg={P.surfaceContainerLow}
              borderRadius="xl"
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              p={6}
            >
              {/* Header: icon + title (count) */}
              <Flex alignItems="center" justifyContent="space-between" mb={4}>
                <Flex alignItems="center" gap={2}>
                  <Box color={P.primaryContainer} flexShrink={0}>
                    <BadgeCheck size={18} />
                  </Box>
                  <Text tid="programaciones.detail.geologistsTitle" values={{ count: schedule.geologists?.length ?? 0 }} fontFamily="'Bricolage Grotesque', sans-serif" fontWeight="700" fontSize="lg" color={P.primaryContainer} />
                </Flex>
              </Flex>

              {(!schedule.geologists || schedule.geologists.length === 0) ? (
                <Text tid="programaciones.detail.noGeologists" fontSize="sm" color={P.onSurfaceVariant} />
              ) : (
                <VStack gap={3} alignItems="stretch">
                  {schedule.geologists.map((g) => {
                    const initials = g.fullName
                      .split(' ')
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((w) => w[0])
                      .join('')
                      .toUpperCase()
                    return (
                      <Flex
                        key={g.id}
                        alignItems="center"
                        gap={3}
                        bg={P.surfaceContainerLowest}
                        p={3}
                        borderRadius="lg"
                        cursor="pointer"
                        _hover={{ bg: 'white' }}
                        transition="background 0.15s"
                      >
                        {/* Avatar */}
                        <Flex
                          w="40px"
                          h="40px"
                          borderRadius="full"
                          bg={P.primaryContainer}
                          color={P.onPrimary}
                          alignItems="center"
                          justifyContent="center"
                          fontSize="13px"
                          fontWeight="800"
                          flexShrink={0}
                          fontFamily="'Bricolage Grotesque', sans-serif"
                        >
                          {initials}
                        </Flex>
                        {/* Info */}
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" fontWeight="600" color={P.onSurface} truncate>
                            {g.fullName}
                          </Text>
                          <Text fontSize="11px" color={P.onSurfaceVariant}>
                            {g.colegiatureCode ? t('programaciones.detail.geologistCip', { code: g.colegiatureCode }) : t('programaciones.detail.geologistRole')}
                          </Text>
                        </Box>
                        <BadgeCheck size={18} color={P.primaryContainer} />
                      </Flex>
                    )
                  })}
                </VStack>
              )}
            </MotionBox>

          </VStack>
        </Box>

        {/* ── Right column (8 / 12) ── */}
        <Box
          style={{ gridColumn: 'span 8' } as React.CSSProperties}
        >
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            bg={P.surfaceContainerLow}
            borderRadius="xl"
            overflow="hidden"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          >
            {/* Section header with icon */}
            <Flex alignItems="center" justifyContent="space-between" px={6} pt={6} pb={5}>
              <Flex alignItems="center" gap={2}>
                <Box color={P.primaryContainer} flexShrink={0}>
                  <MapIcon size={18} />
                </Box>
                <Text tid="programaciones.detail.miningRightsBySupplierTitle" values={{ count: schedule.details?.length ?? 0 }} fontFamily="'Bricolage Grotesque', sans-serif" fontWeight="700" fontSize="lg" color={P.primaryContainer} />
              </Flex>
            </Flex>

            {/* DM groups */}
            <Box px={6} pb={6}>
              {detailsByCompany.size === 0 ? (
                <Text tid="programaciones.detail.noMiningRights" fontSize="sm" color={P.onSurfaceVariant} />
              ) : (
                <VStack gap={5} alignItems="stretch">
                  {Array.from(detailsByCompany.entries()).map(([companyId, group], groupIdx) => (
                    <MotionBox
                      key={companyId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: 0.22 + groupIdx * 0.06 }}
                    >
                      {/* Company header */}
                      <Flex alignItems="center" gap={3} mb={3} px={2}>
                        <Text
                          fontSize="sm"
                          fontWeight="800"
                          color={P.primaryContainer}
                          fontFamily="'Bricolage Grotesque', sans-serif"
                        >
                          {group.companyName}
                        </Text>
                        <Box
                          bg={P.secondaryContainer}
                          color={P.onSecondaryContainer}
                          px={2}
                          py="2px"
                          borderRadius="md"
                          fontSize="10px"
                          fontWeight="700"
                          flexShrink={0}
                        >
                          {group.items.length === 1
                            ? t('programaciones.detail.dmsAssigned', { count: group.items.length })
                            : t('programaciones.detail.dmsAssignedPlural', { count: group.items.length })}
                        </Box>
                        <Box flex={1} borderBottom="1px solid" borderColor={`${P.outlineVariant}30`} />
                      </Flex>

                      {/* DM cards */}
                      <VStack gap={3} alignItems="stretch">
                        {group.items.map((d) => {
                          const detailStatusLabel = DETAIL_STATUSES.find((s) => s.value === d.detailStatus)?.label ?? t('programaciones.detail.unknown')
                          return (
                            <Box
                              key={d.id}
                              bg={P.surfaceContainerLowest}
                              p={4}
                              borderRadius="lg"
                              border="1px solid transparent"
                              _hover={{ borderColor: `${P.primaryContainer}18` }}
                              transition="all 0.15s"
                              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                            >
                              <Flex
                                alignItems={{ base: 'flex-start', md: 'center' }}
                                justifyContent="space-between"
                                gap={4}
                                flexDirection={{ base: 'column', md: 'row' }}
                              >
                                {/* Left: icon + info */}
                                <Flex alignItems="center" gap={4}>
                                  <Flex
                                    w="48px"
                                    h="48px"
                                    bg={P.surfaceContainer}
                                    alignItems="center"
                                    justifyContent="center"
                                    borderRadius="md"
                                    flexShrink={0}
                                  >
                                    <MapIcon size={20} color={P.secondary} />
                                  </Flex>
                                  <Box>
                                    <Text fontWeight="700" color={P.onSurface} fontSize="sm">
                                      {d.miningRightName}
                                    </Text>
                                    {d.miningRightCode && (
                                      <Text fontSize="xs" color={P.onSurfaceVariant} fontFamily="monospace" mt="1px">
                                        {d.miningRightCode}
                                      </Text>
                                    )}
                                    {d.contactReference && (
                                      <Text fontSize="xs" color={P.secondary} mt="2px">
                                        {d.contactReference}
                                      </Text>
                                    )}
                                  </Box>
                                </Flex>

                                {/* Right: status + button */}
                                <Flex alignItems="center" gap={3} flexShrink={0}>
                                  <StatusBadge label={detailStatusLabel} />
                                  {d.detailStatus >= 2 && (
                                    <Flex
                                      as="button"
                                      alignItems="center"
                                      gap={2}
                                      px={4}
                                      py={2}
                                      borderRadius="md"
                                      fontSize="13px"
                                      fontWeight="700"
                                      bg={P.primaryContainer}
                                      color={P.onPrimary}
                                      cursor="pointer"
                                      _hover={{ opacity: 0.85 }}
                                      transition="opacity 0.15s"
                                      onClick={() => navigate(`/app/programaciones/${id}/details/${d.id}/visita`)}
                                    >
                                      {t('programaciones.detail.viewVisit')}
                                      <ArrowRight size={14} />
                                    </Flex>
                                  )}
                                </Flex>
                              </Flex>
                            </Box>
                          )
                        })}
                      </VStack>
                    </MotionBox>
                  ))}
                </VStack>
              )}
            </Box>

            {/* Map with all polygons and component pins */}
            <Box mx={6} mb={6} borderRadius="xl" overflow="hidden" h="280px" position="relative">
              <KMap defaultCenter={{ lat: -12, lng: -76 }} defaultZoom={6} mapTypeId="hybrid">
                {mapPolygons.map((poly) => (
                  <MapPolygon
                    key={`poly-${poly.id}`}
                    paths={poly.paths}
                    strokeColor={P.primaryContainer}
                    fillOpacity={0.15}
                  />
                ))}
                {componentPins.map((c) => (
                  <AdvancedMarker
                    key={`comp-${c.id}`}
                    position={c.pos}
                  >
                    <MarkerTooltip title={c.typeName ?? 'Componente'} subtitle={c.name}>
                      <ComponentPin code={c.typeCode} size={34} />
                    </MarkerTooltip>
                  </AdvancedMarker>
                ))}
                <FitBounds points={mapAllPoints} padding={30} />
              </KMap>
            </Box>
          </MotionBox>
        </Box>
      </Grid>
    </Box>
  )
}
